export type RouteApplicationJoinRow = {
  sortOrder?: number | null;
  applicationId?: number | null;
  application?: {
    id?: number | null;
    applicationId?: number | null;
    internalNumber?: string | null;
    number?: string | null;
    applicationNumber?: string | null;
    applicationDate?: string | null;
    client?: {
      id?: number | null;
      name?: string | null;
      shortName?: string | null;
    } | null;
    clientId?: number | null;
  } | null;
};

export type CompactRouteFieldSummary = {
  display: string;
  tooltipLines: string[];
  primaryLabel: string;
  overflowCount: number;
  count: number;
};

export type CompactRouteClientsSummary = CompactRouteFieldSummary & {
  primaryClientId: number | null;
  uniqueClientIds: number[];
};

function resolveApplicationId(row: RouteApplicationJoinRow): number | null {
  const app = row.application;
  if (app?.id != null && Number.isFinite(Number(app.id))) return Number(app.id);
  if (app?.applicationId != null && Number.isFinite(Number(app.applicationId))) {
    return Number(app.applicationId);
  }
  if (row.applicationId != null && Number.isFinite(Number(row.applicationId))) {
    return Number(row.applicationId);
  }
  return null;
}

function resolveApplicationNumber(row: RouteApplicationJoinRow): string | null {
  const app = row.application;
  const fromFields =
    (app?.internalNumber && String(app.internalNumber).trim()) ||
    (app?.number && String(app.number).trim()) ||
    (app?.applicationNumber && String(app.applicationNumber).trim()) ||
    null;
  if (fromFields) return fromFields;
  const id = resolveApplicationId(row);
  return id != null ? String(id) : null;
}

function resolveClientName(row: RouteApplicationJoinRow): string | null {
  const client = row.application?.client;
  if (!client) return null;
  const shortName = client.shortName != null ? String(client.shortName).trim() : '';
  const name = client.name != null ? String(client.name).trim() : '';
  return shortName || name || null;
}

function resolveClientId(row: RouteApplicationJoinRow): number | null {
  const app = row.application;
  if (app?.client?.id != null && Number.isFinite(Number(app.client.id))) return Number(app.client.id);
  if (app?.clientId != null && Number.isFinite(Number(app.clientId))) return Number(app.clientId);
  return null;
}

/** Preserve API order; order by row.sortOrder only when the field exists in DTO rows. */
export function orderRouteApplicationRows(
  applications: RouteApplicationJoinRow[] | null | undefined
): RouteApplicationJoinRow[] {
  if (!applications?.length) return [];
  const rows = applications.filter(Boolean);
  const hasSortOrder = rows.some(
    (row) => row.sortOrder != null && Number.isFinite(Number(row.sortOrder))
  );
  if (!hasSortOrder) return rows;
  return [...rows].sort((a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0));
}

export function formatRouteApplicationsCompact(
  applications: RouteApplicationJoinRow[] | null | undefined
): CompactRouteFieldSummary {
  const ordered = orderRouteApplicationRows(applications);
  const numbers = ordered
    .map(resolveApplicationNumber)
    .filter((value): value is string => Boolean(value && value !== '—'));

  if (numbers.length === 0) {
    return {
      display: '—',
      tooltipLines: [],
      primaryLabel: '—',
      overflowCount: 0,
      count: 0
    };
  }

  const primaryLabel = numbers[0];
  const overflowCount = Math.max(0, numbers.length - 1);
  const display = overflowCount > 0 ? `${primaryLabel} (+${overflowCount})` : primaryLabel;

  return {
    display,
    tooltipLines: numbers,
    primaryLabel,
    overflowCount,
    count: numbers.length
  };
}

export function formatRouteClientsCompact(
  applications: RouteApplicationJoinRow[] | null | undefined,
  fallbackClient?: { id?: number | null; name?: string | null; shortName?: string | null } | null
): CompactRouteClientsSummary {
  const ordered = orderRouteApplicationRows(applications);
  const seenIds = new Set<number>();
  const uniqueNames: string[] = [];
  const uniqueClientIds: number[] = [];

  for (const row of ordered) {
    const name = resolveClientName(row);
    if (!name) continue;
    const id = resolveClientId(row);
    if (id != null) {
      if (seenIds.has(id)) continue;
      seenIds.add(id);
      uniqueClientIds.push(id);
    } else if (uniqueNames.includes(name)) {
      continue;
    }
    uniqueNames.push(name);
  }

  if (uniqueNames.length === 0 && fallbackClient) {
    const fallbackName =
      (fallbackClient.shortName != null && String(fallbackClient.shortName).trim()) ||
      (fallbackClient.name != null && String(fallbackClient.name).trim()) ||
      null;
    if (fallbackName) {
      uniqueNames.push(fallbackName);
      if (fallbackClient.id != null && Number.isFinite(Number(fallbackClient.id))) {
        uniqueClientIds.push(Number(fallbackClient.id));
      }
    }
  }

  if (uniqueNames.length === 0) {
    return {
      display: '—',
      tooltipLines: [],
      primaryLabel: '—',
      overflowCount: 0,
      count: 0,
      primaryClientId: null,
      uniqueClientIds: []
    };
  }

  const primaryLabel = uniqueNames[0];
  const overflowCount = Math.max(0, uniqueNames.length - 1);
  const display = overflowCount > 0 ? `${primaryLabel} (+${overflowCount})` : primaryLabel;

  return {
    display,
    tooltipLines: uniqueNames,
    primaryLabel,
    overflowCount,
    count: uniqueNames.length,
    primaryClientId: uniqueClientIds[0] ?? null,
    uniqueClientIds
  };
}

export function resolvePrimaryApplicationFromRows(
  applications: RouteApplicationJoinRow[] | null | undefined
): RouteApplicationJoinRow['application'] | null {
  const ordered = orderRouteApplicationRows(applications);
  return ordered[0]?.application ?? null;
}
